import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { isEmpty, pickBy } from 'lodash';
import { CompaniesService, Company } from 'api';
import {
  FUND_INVESTMENT_SERVICES_ERROR_MESSAGES,
  UPDATE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE,
  UPDATE_FUND_INVESTMENT_DEFAULT_SUCCESS_MESSAGE,
} from 'common/constants/services/fundInvestments';
import { SuperAgentError } from 'common/types/services';
import { getNumberValue, getStringValue, throwError } from 'utilities';
import {
  UpdateFundInvestmentParams,
  UpdateFundInvestmentResponse,
  UseUpdateFundInvestmentReturn,
} from './useUpdateFundInvestment.types';

const updateFundInvestment = async (
  params: UpdateFundInvestmentParams
): Promise<UpdateFundInvestmentResponse | null> => {
  const { fundInvestment } = params;

  if (isEmpty(pickBy(fundInvestment))) {
    return throwError(UPDATE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE);
  }

  try {
    const { id, ...fundInvestmentUpdates } = fundInvestment;

    const updatedFundInvestment = await CompaniesService.apiCompaniesUpdate(
      getNumberValue(id),
      fundInvestmentUpdates as Company
    );

    if (updatedFundInvestment?.id) {
      return {
        fundInvestment: updatedFundInvestment,
        responseMessage: UPDATE_FUND_INVESTMENT_DEFAULT_SUCCESS_MESSAGE(getStringValue(updatedFundInvestment?.name)),
      } as UpdateFundInvestmentResponse;
    }

    return throwError(UPDATE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE);
  } catch (error) {
    const { status } = error as SuperAgentError;

    return throwError(
      FUND_INVESTMENT_SERVICES_ERROR_MESSAGES?.[getNumberValue(status)] ?? UPDATE_FUND_INVESTMENT_DEFAULT_ERROR_MESSAGE,
      error
    );
  }
};

const useUpdateFundInvestment = (): UseUpdateFundInvestmentReturn => {
  const queryClient = useQueryClient();

  const updateFundInvestmentMutation: UseMutationResult<
    UpdateFundInvestmentResponse | null, // Response type (null if error)
    Error, // Error type
    UpdateFundInvestmentParams // Parameters type
  > = useMutation({
    mutationFn: updateFundInvestment,
  });

  const { isSuccess, isError, mutateAsync } = updateFundInvestmentMutation;

  if (isSuccess) {
    // Invalidate queries to refetch the data after mutation success
    queryClient.invalidateQueries({ queryKey: [] });

    // Clean the mutation internal state
    updateFundInvestmentMutation.reset();
  }

  return {
    isError,
    mutateAsync,
  };
};

export default useUpdateFundInvestment;
